import React from 'react';
import {
    Mic, Music, Headphones, Volume2, Zap, Sparkles,
    X, Upload, RotateCcw, Layers
} from 'lucide-react';

const TrackSelectionModal = ({ isOpen, onClose, onSelectTrack, t }) => {
    if (!isOpen) return null;

    const trackCategories = [
        {
            id: 'voice-mic',
            name: t.voiceAndMic || 'Voice & Mic',
            icon: Mic,
            color: '#EF4444',
            description: t.voiceMicDesc || 'Record vocals and microphone input',
            type: 'audio',
            instrument: null
        },
        {
            id: 'keys',
            name: t.keys || 'Keys',
            icon: Music,
            color: '#3B82F6',
            description: t.keysDesc || 'Piano, organ, and keyboard instruments',
            type: 'midi',
            instrument: 'piano'
        },
        {
            id: 'bass-808',
            name: t.bassAnd808 || 'Bass & 808',
            icon: Volume2,
            color: '#10B981',
            description: t.bassDesc || 'Bass synthesizers and 808 drums',
            type: 'midi',
            instrument: 'bass'
        },
        {
            id: 'guitar',
            name: t.guitar || 'Guitar',
            icon: Music,
            color: '#F59E0B',
            description: t.guitarDesc || 'Electric and acoustic guitar',
            type: 'audio',
            instrument: null
        },
        {
            id: 'drums-machines',
            name: t.drumsAndMachines || 'Drums & Machines',
            icon: Zap,
            color: '#EF4444',
            description: t.drumsDesc || 'Drum kits and drum machines',
            type: 'midi',
            instrument: 'drums'
        },
        {
            id: 'guitar-bass-amp',
            name: t.guitarBassAmp || 'Guitar/Bass Amp',
            icon: Layers,
            color: '#8B5CF6',
            description: t.ampDesc || 'Amplified guitar and bass',
            type: 'audio',
            instrument: null
        },
        {
            id: 'synth',
            name: t.synth || 'Synth',
            icon: Sparkles,
            color: '#8B5CF6',
            description: t.synthDesc || 'Synthesizers and electronic sounds',
            type: 'midi',
            instrument: 'synth'
        },
        {
            id: 'orchestral',
            name: t.orchestral || 'Orchestral',
            icon: Music,
            color: '#6366F1',
            description: t.orchestralDesc || 'Orchestral and classical instruments',
            type: 'midi',
            instrument: 'piano'
        }
    ];

    const handleTrackSelect = (category) => {
        onSelectTrack(category.type, category.instrument, category.name);
        onClose();
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: '#1e293b',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                border: '1px solid #334155',
                position: 'relative'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '30px'
                }}>
                    <h2 style={{
                        margin: 0,
                        color: '#e2e8f0',
                        fontSize: '24px',
                        fontWeight: 'bold'
                    }}>
                        {t.addNewTrack || 'Add new track'}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#9ca3af',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Track Categories Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '16px',
                    marginBottom: '30px'
                }}>
                    {trackCategories.map((category) => {
                        const IconComponent = category.icon;
                        return (
                            <div
                                key={category.id}
                                onClick={() => handleTrackSelect(category)}
                                style={{
                                    backgroundColor: '#334155',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    border: '2px solid transparent',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    minHeight: '140px',
                                    justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#475569';
                                    e.target.style.borderColor = category.color;
                                    e.target.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#334155';
                                    e.target.style.borderColor = 'transparent';
                                    e.target.style.transform = 'translateY(0)';
                                }}
                            >
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    backgroundColor: category.color,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '12px'
                                }}>
                                    <IconComponent size={32} color="white" />
                                </div>
                                <h3 style={{
                                    margin: '0 0 8px 0',
                                    color: '#e2e8f0',
                                    fontSize: '16px',
                                    fontWeight: '600'
                                }}>
                                    {category.name}
                                </h3>
                                <p style={{
                                    margin: 0,
                                    color: '#9ca3af',
                                    fontSize: '12px',
                                    lineHeight: '1.4'
                                }}>
                                    {category.description}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Bottom Actions */}
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    paddingTop: '20px',
                    borderTop: '1px solid #334155'
                }}>
                    <button
                        onClick={() => {
                            // Handle import file
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'audio/*';
                            input.onchange = (e) => {
                                if (e.target.files[0]) {
                                    onSelectTrack('audio', null, 'Imported Audio', e.target.files[0]);
                                    onClose();
                                }
                            };
                            input.click();
                        }}
                        style={{
                            backgroundColor: 'transparent',
                            border: '2px solid #475569',
                            borderRadius: '8px',
                            color: '#e2e8f0',
                            padding: '12px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.borderColor = '#64748b';
                            e.target.style.backgroundColor = '#334155';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.borderColor = '#475569';
                            e.target.style.backgroundColor = 'transparent';
                        }}
                    >
                        <Upload size={16} />
                        {t.importFile || 'Import file'}
                    </button>

                    <button
                        onClick={() => {
                            // Handle loop library - placeholder for now
                            alert(t.loopLibraryComingSoon || 'Loop library coming soon!');
                        }}
                        style={{
                            backgroundColor: 'transparent',
                            border: '2px solid #475569',
                            borderRadius: '8px',
                            color: '#e2e8f0',
                            padding: '12px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.borderColor = '#64748b';
                            e.target.style.backgroundColor = '#334155';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.borderColor = '#475569';
                            e.target.style.backgroundColor = 'transparent';
                        }}
                    >
                        <RotateCcw size={16} />
                        {t.loopLibrary || 'Loop library'}
                    </button>

                    <button
                        onClick={() => {
                            // Handle sampler - placeholder for now
                            alert(t.samplerComingSoon || 'Sampler coming soon!');
                        }}
                        style={{
                            backgroundColor: 'transparent',
                            border: '2px solid #475569',
                            borderRadius: '8px',
                            color: '#e2e8f0',
                            padding: '12px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.borderColor = '#64748b';
                            e.target.style.backgroundColor = '#334155';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.borderColor = '#475569';
                            e.target.style.backgroundColor = 'transparent';
                        }}
                    >
                        <Layers size={16} />
                        {t.sampler || 'Sampler'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrackSelectionModal; 